import React, { useRef, useState } from 'react';
import {
	ArrowUpTrayIcon,
	DocumentIcon,
	XMarkIcon,
} from '@heroicons/react/24/outline';

const ACCEPT = '.pdf,.jpg,.jpeg,.png,.heic,.heif,.webp';
const ALLOWED = [
	'application/pdf',
	'image/jpeg',
	'image/png',
	'image/heic',
	'image/heif',
	'image/webp',
];
const MAX_BYTES = 10 * 1024 * 1024;

function prettySize(bytes) {
	if (bytes < 1024) return `${bytes} B`;
	if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
	return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

/**
 * Optional drag-and-drop file picker for a utility bill.
 * Controlled: pass `file` and `onFile(file|null)`. Validates type/size and
 * surfaces an inline error. `dark` for use on dark backgrounds.
 */
export default function BillDropzone({ file, onFile, dark = false, label }) {
	const inputRef = useRef(null);
	const [dragging, setDragging] = useState(false);
	const [error, setError] = useState(null);

	const validateAndSet = (f) => {
		if (!f) return;
		if (!ALLOWED.includes(f.type)) {
			setError('Please upload a PDF or image (JPG, PNG, HEIC).');
			return;
		}
		if (f.size > MAX_BYTES) {
			setError('File is too large. Max 10 MB.');
			return;
		}
		setError(null);
		onFile(f);
	};

	const onDrop = (e) => {
		e.preventDefault();
		setDragging(false);
		validateAndSet(e.dataTransfer.files?.[0]);
	};

	const baseRing = dark ? 'ring-white/15' : 'ring-ink-200';
	const hoverRing = dark ? 'ring-primary-400 bg-white/5' : 'ring-primary-400 bg-primary-50/40';
	const textMuted = dark ? 'text-white/60' : 'text-ink-500';
	const textMain = dark ? 'text-white' : 'text-ink-800';

	if (file) {
		return (
			<div
				className={`flex items-center gap-3 rounded-xl px-4 py-3 ring-1 ${baseRing} ${
					dark ? 'bg-white/5' : 'bg-white'
				}`}
			>
				<DocumentIcon className='h-8 w-8 flex-none text-primary-500' />
				<div className='min-w-0 flex-1'>
					<p className={`truncate text-sm font-medium ${textMain}`}>{file.name}</p>
					<p className={`text-xs ${textMuted}`}>{prettySize(file.size)}</p>
				</div>
				<button
					type='button'
					onClick={() => {
						onFile(null);
						if (inputRef.current) inputRef.current.value = '';
					}}
					className={`rounded p-1 ${textMuted} hover:text-rose-500`}
					aria-label='Remove file'
				>
					<XMarkIcon className='h-5 w-5' />
				</button>
			</div>
		);
	}

	return (
		<div>
			<button
				type='button'
				onClick={() => inputRef.current?.click()}
				onDragOver={(e) => {
					e.preventDefault();
					setDragging(true);
				}}
				onDragLeave={() => setDragging(false)}
				onDrop={onDrop}
				className={`flex w-full flex-col items-center justify-center gap-2 rounded-xl border-2 border-dashed px-6 py-8 text-center transition-colors ${
					dragging
						? `border-transparent ring-2 ${hoverRing}`
						: `${dark ? 'border-white/20 hover:border-white/40' : 'border-ink-200 hover:border-primary-300'}`
				}`}
			>
				<ArrowUpTrayIcon className={`h-7 w-7 ${dark ? 'text-primary-400' : 'text-primary-500'}`} />
				<span className={`text-sm font-medium ${textMain}`}>
					{label || 'Drag & drop your utility bill'}
				</span>
				<span className={`text-xs ${textMuted}`}>
					or click to browse · PDF or image · up to 10 MB
				</span>
			</button>
			<input
				ref={inputRef}
				type='file'
				accept={ACCEPT}
				className='hidden'
				onChange={(e) => validateAndSet(e.target.files?.[0])}
			/>
			{error && <p className='mt-2 text-xs text-rose-500'>{error}</p>}
		</div>
	);
}
